import {
  createReference,
  MedplumClient,
  normalizeErrorString,
  ProfileResource,
} from "@medplum/core";

import { Extensions } from "@/fhir/enums/extensions";
import { ifMatchVersionOptions } from "@/utils/medplum-if-match";
import { isMedplumIfMatchConflictError } from "@/utils/medplum-if-match-conflict";

import { NO_NOTIFICATIONS } from "../types/NotificationsPreference";

export type SaveNotificationPreferencesResult =
  | { ok: true; data: ProfileResource }
  | { ok: false; reason: "stale_profile" | "save_failed"; details?: { message?: string } };

/**
 * Reads the latest stored version of the signed-in user's profile resource from the server. The
 * profile cached on the Medplum client keeps the version loaded at sign-in, and the client's read
 * cache can hold a version another writer has since replaced, so this read bypasses both.
 *
 * @param medplum - Authenticated Medplum client.
 * @param profile - The signed-in user's profile resource.
 * @returns The latest stored version of the profile resource.
 */
export async function fetchNotificationPreferencesProfile(
  medplum: MedplumClient,
  profile: ProfileResource,
): Promise<ProfileResource> {
  return medplum.readReference(createReference(profile), { cache: "no-cache" });
}

/**
 * Writes the notification preferences extension on a profile resource, replacing any previous
 * value and keeping every other extension. An empty selection is stored as `NO_NOTIFICATIONS`,
 * because a missing extension means the user is opted in to everything. The write is guarded with
 * `If-Match` on the version that was read, so a profile changed since that read is refused
 * instead of overwritten.
 *
 * @param medplum - Authenticated Medplum client.
 * @param profile - The profile resource to update, as it was read.
 * @param selectedPreferenceIds - Notification preference ids the user opted in to.
 * @returns The updated profile resource, `stale_profile` when the profile changed since it was
 *   read, or `save_failed` for any other failure.
 */
export async function saveNotificationPreferences(
  medplum: MedplumClient,
  profile: ProfileResource,
  selectedPreferenceIds: string[],
): Promise<SaveNotificationPreferencesResult> {
  const otherExtensions = (profile.extension ?? []).filter(
    (extension) => extension.url !== Extensions.NotificationPreferences,
  );
  const preferencesValue =
    selectedPreferenceIds.length > 0 ? selectedPreferenceIds.join(",") : NO_NOTIFICATIONS;

  try {
    const updatedProfile = await medplum.updateResource<ProfileResource>(
      {
        ...profile,
        extension: [
          ...otherExtensions,
          { url: Extensions.NotificationPreferences, valueString: preferencesValue },
        ],
      },
      ifMatchVersionOptions(profile),
    );

    return { ok: true, data: updatedProfile };
  } catch (error) {
    return {
      ok: false,
      reason: isMedplumIfMatchConflictError(error) ? "stale_profile" : "save_failed",
      details: { message: normalizeErrorString(error) },
    };
  }
}
